import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { TextField, Grid, Paper, FormControl, InputLabel, FilledInput, InputAdornment, IconButton, Input } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';

import { useAppSelector } from '@store/hooks';
import { LoadingButton } from '@components';
import { AlertError } from '@components/atoms/alerts';
import { PathsEnum } from '@routes/enumerators/paths.enum';
import { ICredentials } from '@core/services/login/interfaces';
import { selectorAuth } from '@core/slice/login.slice';
import { loginService } from '@core/services';
import { colors } from '@assets/colors';

import { ReactComponent as LogoSTZ } from '@assets/logo.svg';
import { ReactComponent as LogoOuze } from '@assets/ouze.svg';
import { isFilled } from '@core/utils';

interface ILoginProps {}
export const Login: React.FC<ILoginProps> = () => {
    const [credentials, setCredentials] = useState<ICredentials>({
        matricula: '',
        password: ''
    });
    const {
        auth: { token },
        loading,
        error
    } = useAppSelector(selectorAuth);
    const navigate = useNavigate();
    const location = useLocation();
    const redirectTo = location.state?.previousUrl || PathsEnum.COLABORADOR;

    useEffect(() => {
        if (token) {
            navigate(redirectTo);
        }
    }, [token]);

    const handlingOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setCredentials((prev) => {
            const updated = { ...prev, [name]: value };

            if (name === 'matricula' && value.length === 11) {
                updated.cpf = value;
            } else if (name === 'matricula' && value.length !== 11) {
                updated.cpf = undefined;
            }

            return updated;
        });
    };

    const [showPassword, setShowPassword] = React.useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const isInvalid = () => !(credentials?.matricula && credentials?.password);
    const handlingSubmit = () => {
        if (isFilled(credentials.cpf)) {
            credentials.matricula = undefined;
        }

        loginService.login(credentials);
    };

    const darkTheme = createTheme({
        palette: {
            mode: 'dark',
            primary: {
                ...colors.theme.primary
            },
            secondary: {
                ...colors.theme.secondary
            }
        },
        typography: {
            fontFamily: ['-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif', '"Apple Color Emoji"', '"Segoe UI Emoji"', '"Segoe UI Symbol"'].join(
                ','
            )
        },
        shape: {
            borderRadius: 8
        }
    });

    return (
        <ThemeProvider theme={darkTheme}>
            <Box sx={{ bgcolor: '#121212', height: '100vh', display: 'flex', alignItems: 'center' }}>
                <Container maxWidth="xs">
                    <Paper elevation={0} sx={{ py: 5, px: 2 }}>
                        <Grid container rowSpacing={2} columnSpacing={2}>
                            <Grid item sm={12} xs={12} sx={{ alignItems: 'center', mr: 1, height: '56px', justifyContent: 'center', display: 'flex' }}>
                                <LogoSTZ style={{ height: '32px', fill: '#fff', padding: '16px 0', marginRight: '16px' }} />
                                <LogoOuze style={{ height: '24px', padding: '16px 0' }} />
                            </Grid>
                            <Grid item sm={12}>
                                <div style={{ padding: 15 }}>
                                    <Grid container rowSpacing={3}>
                                        <Input type="hidden" id="cpf" name="cpf" value={credentials.cpf || ''} onChange={handlingOnChange} />
                                        <Grid item xs={12} sm={12}>
                                            <TextField
                                                id="matricula"
                                                name="matricula"
                                                label="Matricula"
                                                variant="filled"
                                                value={credentials.matricula || ''}
                                                onChange={handlingOnChange}
                                                color="secondary"
                                                autoFocus
                                                fullWidth
                                                onKeyPress={(ev) => {
                                                    if (ev.key === 'Enter') {
                                                        handlingSubmit();
                                                        ev.preventDefault();
                                                    }
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12}>
                                            <FormControl variant="filled" fullWidth>
                                                <InputLabel htmlFor="password" color="secondary">
                                                    Senha
                                                </InputLabel>
                                                <FilledInput
                                                    id="password"
                                                    type={showPassword ? 'text' : 'password'}
                                                    name="password"
                                                    color="secondary"
                                                    onChange={handlingOnChange}
                                                    onKeyPress={(ev) => {
                                                        if (ev.key === 'Enter') {
                                                            handlingSubmit();
                                                            ev.preventDefault();
                                                        }
                                                    }}
                                                    endAdornment={
                                                        <InputAdornment position="end">
                                                            <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    }
                                                />
                                            </FormControl>
                                        </Grid>

                                        <AlertError error={error} withinGrid />

                                        <Grid item xs={12} sm={12}>
                                            <LoadingButton type="submit" label="ENTRAR" color="secondary" loading={loading} size="medium" disabled={isInvalid() || loading} onClick={handlingSubmit} />
                                        </Grid>
                                    </Grid>
                                </div>
                            </Grid>
                        </Grid>
                    </Paper>
                </Container>
            </Box>
        </ThemeProvider>
    );
};
